// import pl = egr.wcf.pl;
import cf = egr.wcf.cf;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import eaiws = egr.wcf.eaiws;
import room = egr.wcf.room;

import { Overlay } from '../constants/Enums';
import { VisibilityStore } from '../stores/VisibilityStore';

export interface ColorRGB {
    r: number;
    g: number;
    b: number;
}

export abstract class GlobalHelper {

    public static coreApp: core.Application;
    public static eaiwsSession: eaiws.EaiwsSession;
    public static articleManager: cf.ArticleManager;
    public static roomManager: room.RoomManager;

    // Note: should be removed later on and can't be initilized
    public static floorPlane: BABYLON.Mesh;
    public static grid: BABYLON.Mesh;
    public static floorPlan: any;
    public static arManager: ArStateManager = ArStore;
    public static lineMesh: BABYLON.LinesMesh;

    public static get activeView(): core.view.View {
        if (GlobalHelper.coreApp.viewer.activeView == null) {
            throw new Error('no active view');
        }
        return GlobalHelper.coreApp.viewer.activeView;
    }

    private static shadowPlane: core.rendering.ShadowPlane;

    public static initialize(containerId: string): void {
        const viewerContainer: HTMLDivElement = document.getElementById(containerId) as HTMLDivElement;

        const appOptions: core.AppInitOptions = new core.AppInitOptions();
        appOptions.adaptToDeviceRatio = true;
        appOptions.autoResizeViewer = false;
        appOptions.disableWebGL2Support = true;

        const rootPath: string = utils.string.getFilePath(window.location.pathname);

        GlobalHelper.coreApp = new core.Application();
        GlobalHelper.coreApp.dataPath = rootPath + 'w-cf/data/';
        GlobalHelper.coreApp.initialize(viewerContainer, appOptions);

        GlobalHelper.coreApp.registerModule(new ProjectSettingsModule());
        GlobalHelper.coreApp.model.eventModified.addListener((): void => {
            // FixMe: ugly workaround
            switch (VisibilityStore.getState().overlay) {
                case Overlay.PENDING:
                case Overlay.LOGIN:
                case Overlay.LOGIN_QUESTION:
                    break;
                default:
                    SceneActions.markSceneAsChanged();
                    break;
            }
        });
        GlobalHelper.coreApp.model.eventModified.addListener(SceneActions.updateUndoRedo);
        GlobalHelper.coreApp.document.eventAfterClear.addListener((): void => {
            SceneActions.setUnsavedChanges(false);
        });

        GlobalHelper.eaiwsSession = new eaiws.EaiwsSession();
        GlobalHelper.articleManager = new cf.ArticleManager(GlobalHelper.coreApp, GlobalHelper.eaiwsSession);

        GlobalHelper.roomManager = new room.RoomManager(GlobalHelper.coreApp, rootPath + 'w-cf-room/data/');
        GlobalHelper.roomManager.resources = new RoomResources();
        GlobalHelper.roomManager.onShowInputField = ArchitectureActions.showInputField;
        GlobalHelper.roomManager.setVisibilityMode(PersistentStore.getState().wall3d ? room.VisibilityMode.Show3d : room.VisibilityMode.Show2d);
        OAPActions.overrideAppCallbacks();

        GlobalHelper.arManager = ArStore;
        ArStore.initialize();

        GlobalHelper.coreApp.theme.mainColorBright = '#e60023';
    }

    public static setFloorPlaneColor(color: ColorRGB): void {
        const tMat: core.mdl.PbrMaterial = GlobalHelper.floorPlane.material as core.mdl.PbrMaterial;
        tMat.albedoColor = new BABYLON.Color3(
            color.r / 255,
            color.g / 255,
            color.b / 255
        ).toLinearSpace();
        // is white?
        const tSum: number = tMat.albedoColor.r + tMat.albedoColor.g + tMat.albedoColor.b;
        if (tSum > 2.9) {
            if (!tMat.unlit) {
                tMat.unlit = true;
                tMat.emissiveColor = new BABYLON.Color3(1, 1, 1);
            }
        } else if (tMat.unlit) {
            tMat.unlit = false;
            tMat.emissiveColor = new BABYLON.Color3(0, 0, 0);
        }
    }

    public static createTestScene(): void {
        const groundType: GroundType = ProjectSettingsStore.getState().groundType || 'solid';
        const tScene: BABYLON.Scene = GlobalHelper.coreApp.model.scene;

        GlobalHelper.grid = utils.planning.createGrid(tScene, 10);
        GlobalHelper.grid.setEnabled(groundType === 'grid');

        // Create floorplane
        const tMat: core.mdl.PbrMaterial = new core.mdl.PbrMaterial('', GlobalHelper.coreApp.model.materials);
        tMat.metallic = 0;
        tMat.roughness = 0.5;
        GlobalHelper.floorPlane = BABYLON.MeshBuilder.CreateGround('floor', { width: 10, height: 10 }, tScene);
        GlobalHelper.floorPlane.isPickable = false;
        GlobalHelper.floorPlane.material = tMat;
        GlobalHelper.floorPlane.position.y = -0.0005;
        GlobalHelper.floorPlane.setEnabled(false);
        GlobalHelper.setFloorPlaneColor(ProjectSettingsStore.defaultGroundColor);

        // Create grid
        GlobalHelper.lineMesh = BABYLON.MeshBuilder.CreateLineSystem(
            'gridLines1',
            {
                lines: [[new BABYLON.Vector3(-5, 0, -5), new BABYLON.Vector3(5, 0, -5),
                    new BABYLON.Vector3(5, 0, 5), new BABYLON.Vector3(-5, 0, 5),
                    new BABYLON.Vector3(-5, 0, -5)]], updatable: false, instance: null
            },
            tScene
        );
        GlobalHelper.lineMesh.color = new BABYLON.Color3(0.85, 0.85, 0.85);
        GlobalHelper.lineMesh.isPickable = false;
        GlobalHelper.lineMesh.parent = GlobalHelper.floorPlane;
        GlobalHelper.shadowPlane = new core.rendering.ShadowPlane(GlobalHelper.coreApp);

        // Create floor plan
        GlobalHelper.floorPlan = new FloorPlan(GlobalHelper.coreApp);
        GlobalHelper.floorPlan.enabled = ProjectSettingsStore.getFloorPlanVisibility();

        GlobalHelper.coreApp.rendering.addShadowGenerator(GlobalHelper.shadowPlane);
    }

    public static stopActiveTool = (): void => {
        if (
            GlobalHelper.coreApp.tools &&
            GlobalHelper.coreApp.tools.activeTool &&
            GlobalHelper.coreApp.tools.activeTool.isRunning()
        ) {
            // ToDo: check if `Globals.coreApp.tools.stopActiveTool()` would be a better choise
            GlobalHelper.coreApp.tools.activeTool.stop();
        }
    };

    public static focusCanvas(): void {
        GlobalHelper.coreApp.viewer.canvas.focus();
    }

    public static setLanguage(language: string): void {
        GlobalHelper.coreApp.i18n.setLanguage(language);
        GlobalHelper.coreApp.i18n.setLocale(language);
    }

    public static getLocaleLengthString(value: number): string | null {
        return (
            utils.number.toLocaleLengthString(
                value as number,
                GlobalHelper.coreApp.i18n.locale,
                GlobalHelper.coreApp.i18n.lengthUnit
            )
        );
    }

    public static getValueFromLocaleLengthString(value: string): number | null {
        return utils.number.fromLocaleLengthString(value, GlobalHelper.coreApp.i18n.lengthUnit);
    }

    public static placeElementsInViewCenter(elements: Array<core.mdl.SceneElement>): void {
        if (GlobalHelper.floorPlan.enabled) {
            const options: utils.planning.PlaceElementsOptions = {};
            options.placingAreaMin = GlobalHelper.floorPlan.placingAreaMin;
            options.placingAreaMax = GlobalHelper.floorPlan.placingAreaMax;
            utils.planning.placeElementsInViewCenter(
                elements, GlobalHelper.activeView, GlobalHelper.coreApp.model,
                options
            );
        } else {
            utils.planning.placeElementsInViewCenter(
                elements, GlobalHelper.activeView, GlobalHelper.coreApp.model
            );
        }
    }

    public static updateDefaultTool(simple: boolean): void {
        // const enabledRotate: boolean = !simple;
        // if (
        //     GlobalHelper.coreApp != null &&
        //     GlobalHelper.coreApp.tools != null &&
        //     GlobalHelper.coreApp.tools.defaultTool != null &&
        //     (GlobalHelper.coreApp.tools.defaultTool as pl.tool.Default).rotateXEnabled !== enabledRotate
        // ) {
        //     (GlobalHelper.coreApp.tools.defaultTool as pl.tool.Default).rotateXEnabled = enabledRotate;
        //     (GlobalHelper.coreApp.tools.defaultTool as pl.tool.Default).rotateZEnabled = enabledRotate;
        //     /* initially set in app.ts */
        //     if (GlobalHelper.coreApp.tools.activeTool === GlobalHelper.coreApp.tools.defaultTool) {
        //         GlobalHelper.coreApp.tools.stopActiveTool();
        //     }
        // }
    }

    public static getArticleCount(): number {
        if (GlobalHelper.coreApp && GlobalHelper.coreApp.model && GlobalHelper.coreApp.model.elements) {
            return GlobalHelper.coreApp.model.elements.filter((checkElement) => {
                return checkElement.category !== core.mdl.ElementCategory.Wall && checkElement.category !== core.mdl.ElementCategory.WallOpening;
            }).length;
        }
        return 0;
    }

    public static getWallCount(): number {
        if (GlobalHelper.coreApp && GlobalHelper.coreApp.model && GlobalHelper.coreApp.model.elements) {
            return GlobalHelper.coreApp.model.elements.filter((checkElement) => {
                return checkElement.category === core.mdl.ElementCategory.Wall;
            }).length;
        }
        return 0;
    }

    public static getSceneElementsCount(): number {
        if (GlobalHelper.coreApp && GlobalHelper.coreApp.model && GlobalHelper.coreApp.model.elements) {
            return GlobalHelper.coreApp.model.elements.length;
        }
        return 0;
    }
}

import { FloorPlan } from './FloorPlan';
import { RoomResources } from './RoomResources';
import { SceneActions } from '../actions/SceneActions';
import { OAPActions } from '../actions/OAPActions';
import { ArchitectureActions } from '../actions/ArchitectureActions';
import { ProjectSettingsModule } from './ProjectSettingsModule';
import { PersistentStore } from '../stores/PersistentStore';
import { ProjectSettingsStore, GroundType } from '../stores/ProjectSettingsStore';
import { ArStore } from '../stores/ArStore';
import { ArStateManager } from '../submodules/ar-manager/ArStateManager';



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/GlobalHelper.ts